// This is a part of the Cadcorp SIS source code. 
// Copyright (c) Cadcorp Limited 1991-2025 
import { IEventConfig, IEventSource } from '../../../../CommonScripts/Abstractions';

/**
* An events object that handles custom events
* @export
*/
export class Events implements IEventSource {
    /**
    * @public
    * @property {object} owner - The owner object for this event object
    * @memberof Cadcorp.Web.Core.Events#
    */
    owner: object;
    /**
    * @private
    * @property {object} m_events - The event object
    * @memberof Cadcorp.Web.Core.Events#
    */
    private m_events: object;

    /**
    * The an instance of the event 
    * @export
    * @param {Cadcorp.Util.EventConfig} config Configuration for this event object
    */
    constructor(config: IEventConfig) {
        this.owner = config.owner;
        this.m_events = {};
    }

    /**
    * Is this event handler registered with this event object
    * @public
    * @function
    * @memberof Cadcorp.Web.Core.Events#
    * @param {string} type - The event type
    * @param {function} handler - The function that will be called when the event is triggered
    * @returns {boolean} true if the event handler has been registered, otherwise false 
    */
    isRegistered(type: string, handler: object): boolean {

        return this.getIndex(type, handler) >= 0;
    }

    /**
    * Gets the index of this event handler
    * @public
    * @function
    * @memberof Cadcorp.Web.Core.Events#
    * @param {string} type - The event type
    * @param {function} handler - The function that will be called when the event is triggered
    * @returns {number} The index of the event handler 
    */
    getIndex(type: string, handler: object): number {
        var index = -1;
        if (this.m_events[type]) {
            for (var c = 0; c < this.m_events[type].length && index === -1; c++) {
                if (this.m_events[type][c] === handler) {
                    index = c;
                }
            }
        }
        return index;
    }

    /**
     * @inheritdoc 
     */
    on(type: string, handler: (eventData: any) => boolean): void {

        if (this.m_events[type]) {
            this.m_events[type].push( handler);
        } else {
            this.m_events[type] = [handler];
        }
    }

    /**
     * @inheritdoc
     */
    un(type: string, handler: (eventData: any) => boolean): void {
        if (this.isRegistered(type, handler)) {
            this.m_events[type].splice(this.getIndex(type,handler), 1);
        }
    }

    /** 
    * @description
    * Trigger a specified registered event.<br />
    * If eventData is supplied, type and object properties will be added.<br />
    * If eventData is null a new object is created with type and object properties.<br />
    * In either case eventData will be passed to all listeners 
    * @public
    * @function
    * @memberof Cadcorp.Web.Core.Events#
    * @param {string} type - The event type
    * @param {?Object} eventData - An object containing event data, will be passed to the listener.
    * @returns {boolean} The success or failure of the event handler
    */
    dispatchEvent(type: string, eventData: any): boolean {
        var retval = true;
        if (this.m_events[type]) {
            var handlers = this.m_events[type];
            if (!eventData) {
                eventData = {};
            }
            eventData.type = type;
            eventData.object = this.owner;
            if (handlers && handlers.length > 0) {
                for (var c = 0; c < handlers.length && retval; c++) {
                    var ret = handlers[c].call(this, eventData);
                    if (ret !== undefined)
                        retval = ret;
                }
            }
        }
        return retval;
    }

    /** Destroys this event object.
    * @public
    * @function
    * @memberof Cadcorp.Web.Core.Events#
    */
    destroy() {
        this.m_events = null;
        this.owner = null;
    }
};

